import { Button, Checkbox, EnvBadge, Input, useToast } from '@/components';
import { listDeviceFileGroups } from '@/features/devices/api';
import { useReleaseProducts } from '@/features/release-config/hooks';
import { apiCall } from '@/lib/api';
import { toggleInSet } from '@/lib/set';
import { createQuery } from '@tanstack/solid-query';
import {
  ApiClientError,
  RELEASE_CAP_GLOBAL_KEY,
  RELEASE_ENVIRONMENTS,
  RELEASE_SCOPED_CAPS,
  type RoleListItem,
  STATIC_PERMISSIONS,
  releaseScopePerm,
} from '@uchat-team-management/shared';
import { For, Show, createMemo, createSignal } from 'solid-js';
import { useCreateRole, useUpdateRole } from './hooks';
import { permissionSections } from './permission-picker';

const useFileGroups = () =>
  createQuery(() => ({
    queryKey: ['device-file-groups'] as const,
    queryFn: () => apiCall(listDeviceFileGroups, {}),
  }));

/**
 * UCH-419/UCH-428: scoped `releases:<slug>:<env>:<cap>` grants as a table
 * matching the permissions table above — one section per environment with a
 * select-all header checkbox, one row per product with a checkbox per
 * capability. A cap whose global key is checked shows disabled checked cells
 * (already covered) and is excluded from the env select-all.
 */
const ScopedReleaseGrants = (props: {
  products: { id: string; slug: string; name: string }[];
  query: string;
  selected: Set<string>;
  toggle: (key: string) => void;
  setKeys: (keys: string[], on: boolean) => void;
  readOnly?: boolean;
}) => {
  const globalHeld = (cap: (typeof RELEASE_SCOPED_CAPS)[number]) =>
    props.selected.has(RELEASE_CAP_GLOBAL_KEY[cap]);
  // Keys the select-alls operate on: globally covered caps are excluded —
  // their cells are disabled and adding scoped keys under them would be noise.
  const matches = (
    env: (typeof RELEASE_ENVIRONMENTS)[number],
    product: (typeof props.products)[number],
    cap: (typeof RELEASE_SCOPED_CAPS)[number],
  ) => {
    const meta = STATIC_PERMISSIONS[RELEASE_CAP_GLOBAL_KEY[cap]];
    return `${product.name} ${releaseScopePerm(product.slug, env, cap)} ${meta.label} ${meta.description}`
      .toLowerCase()
      .includes(props.query);
  };
  const products = (env: (typeof RELEASE_ENVIRONMENTS)[number]) =>
    props.products.filter((p) => RELEASE_SCOPED_CAPS.some((cap) => matches(env, p, cap)));
  const environments = () => RELEASE_ENVIRONMENTS.filter((env) => products(env).length > 0);
  const rowKeys = (
    env: (typeof RELEASE_ENVIRONMENTS)[number],
    product: (typeof props.products)[number],
  ) =>
    RELEASE_SCOPED_CAPS.filter((cap) => matches(env, product, cap) && !globalHeld(cap)).map((cap) =>
      releaseScopePerm(product.slug, env, cap),
    );
  const envKeys = (env: (typeof RELEASE_ENVIRONMENTS)[number]) =>
    products(env).flatMap((p) => rowKeys(env, p));

  return (
    <>
      <span class="font-medium">Scoped release grants</span>
      <Show
        when={environments().length > 0}
        fallback={<p role="status">No matching scoped release grants.</p>}
      >
        <div class="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-800">
          <table class="w-full text-sm">
            <For each={environments()}>
              {(env) => {
                const keys = () => envKeys(env);
                const all = () => keys().length > 0 && keys().every((k) => props.selected.has(k));
                const some = () => keys().some((k) => props.selected.has(k)) && !all();
                const allCovered = () => keys().length === 0;
                return (
                  <tbody class="border-t border-neutral-200 first:border-t-0 dark:border-neutral-800">
                    <tr class="bg-neutral-50 dark:bg-neutral-900/50">
                      <th class="px-3 py-2 text-left font-normal">
                        <Checkbox
                          checked={all() || allCovered()}
                          indeterminate={some()}
                          disabled={allCovered() || props.readOnly}
                          onChange={() => props.setKeys(keys(), !all())}
                          aria-label={`Select all ${env} release grants`}
                          label={<EnvBadge env={env} />}
                        />
                      </th>
                      <th
                        class="px-3 py-2 text-center text-xs font-semibold tracking-wide text-neutral-600 dark:text-neutral-300"
                        title="Select all matching grants in the row"
                      >
                        All
                      </th>
                      <For each={[...RELEASE_SCOPED_CAPS]}>
                        {(cap) => {
                          const meta = STATIC_PERMISSIONS[RELEASE_CAP_GLOBAL_KEY[cap]];
                          return (
                            <th
                              class="px-3 py-2 text-center text-xs font-semibold tracking-wide text-neutral-600 capitalize dark:text-neutral-300"
                              title={`${meta.label} — ${meta.description}`}
                            >
                              {cap}
                              <Show when={globalHeld(cap)}>
                                <span class="block font-normal text-neutral-400 normal-case">
                                  covered by global
                                </span>
                              </Show>
                            </th>
                          );
                        }}
                      </For>
                    </tr>
                    <For each={products(env)}>
                      {(product) => {
                        const keys = () => rowKeys(env, product);
                        const rowAll = () =>
                          keys().length > 0 && keys().every((k) => props.selected.has(k));
                        const rowSome = () =>
                          keys().some((k) => props.selected.has(k)) && !rowAll();
                        const rowCovered = () => keys().length === 0;
                        return (
                          <tr class="border-t border-neutral-100 transition-colors hover:bg-neutral-50 motion-reduce:transition-none dark:border-neutral-800/60 dark:hover:bg-neutral-900/40">
                            <td class="px-3 py-2 whitespace-nowrap">
                              <span class="flex flex-col">
                                <span class="font-medium">{product.name}</span>
                                <code class="text-xs text-neutral-500 dark:text-neutral-400">
                                  {product.slug}
                                </code>
                              </span>
                            </td>
                            <td class="px-3 py-2">
                              <span class="flex justify-center">
                                <Checkbox
                                  checked={rowAll() || rowCovered()}
                                  indeterminate={rowSome()}
                                  disabled={rowCovered() || props.readOnly}
                                  onChange={() => props.setKeys(keys(), !rowAll())}
                                  aria-label={`Select all ${env} grants for ${product.name}`}
                                />
                              </span>
                            </td>
                            <For each={[...RELEASE_SCOPED_CAPS]}>
                              {(cap) => (
                                <td class="px-3 py-2">
                                  <Show
                                    when={matches(env, product, cap)}
                                    fallback={
                                      <span class="block text-center text-neutral-400">—</span>
                                    }
                                  >
                                    <span class="flex justify-center">
                                      <Checkbox
                                        checked={
                                          globalHeld(cap) ||
                                          props.selected.has(
                                            releaseScopePerm(product.slug, env, cap),
                                          )
                                        }
                                        disabled={globalHeld(cap) || props.readOnly}
                                        onChange={() =>
                                          props.toggle(releaseScopePerm(product.slug, env, cap))
                                        }
                                        aria-label={releaseScopePerm(product.slug, env, cap)}
                                      />
                                    </span>
                                  </Show>
                                </td>
                              )}
                            </For>
                          </tr>
                        );
                      }}
                    </For>
                  </tbody>
                );
              }}
            </For>
          </table>
        </div>
      </Show>
    </>
  );
};

export const RoleForm = (props: {
  role?: RoleListItem;
  /** Fully locked view (system admin role, or viewer without roles:update). */
  readOnly?: boolean;
  /** System roles keep their name; the form omits `name` from the PATCH body. */
  nameLocked?: boolean;
  onDone: () => void;
  onCancel: () => void;
}) => {
  const groups = useFileGroups();
  // Disabled products stay renderable so old scoped grants remain editable;
  // on 403 (no releases:view) the scoped block simply doesn't render.
  const releaseProducts = useReleaseProducts(true);
  const create = useCreateRole();
  const update = useUpdateRole();
  const toast = useToast();
  const [name, setName] = createSignal(props.role?.name ?? '');
  const [selected, setSelected] = createSignal<Set<string>>(new Set(props.role?.permissions ?? []));
  const [error, setError] = createSignal<string | null>(null);

  const loading = () => create.isPending || update.isPending;

  // Memoized so unrelated reactive updates don't rebuild every table row.
  const sections = createMemo(() =>
    permissionSections((groups.data?.data ?? []).map((g) => ({ slug: g.slug, name: g.name }))),
  );

  const toggle = (key: string) => {
    if (props.readOnly) return;
    setSelected((cur) => toggleInSet(cur, key));
  };

  const setKeys = (keys: string[], on: boolean) => {
    if (props.readOnly) return;
    setSelected((cur) => {
      const next = new Set(cur);
      for (const key of keys) {
        if (on) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  };

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (props.readOnly) return;
    setError(null);
    if (!name().trim()) {
      setError('Name is required.');
      return;
    }
    const body = {
      permissions: [...selected()],
      ...(props.nameLocked ? {} : { name: name().trim() }),
    };
    try {
      const role = props.role;
      if (role) {
        await update.mutateAsync({ roleId: role.id, body });
        toast.success('Role updated');
      } else {
        await create.mutateAsync(body);
        toast.success('Role created');
      }
      props.onDone();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.error.message);
      else setError((err as Error).message ?? 'Save failed');
    }
  };

  return (
    <form onSubmit={onSubmit} class="flex flex-col gap-4">
      <Input
        label="Name"
        value={name()}
        onInput={(e) => setName(e.currentTarget.value)}
        required
        disabled={props.readOnly || props.nameLocked}
        class="max-w-sm"
      />
      <div class="flex flex-col gap-2 text-sm">
        <span class="font-medium">Permissions</span>
        <For each={sections().map((section) => section.title)}>
          {(title) => {
            const [search, setSearch] = createSignal('');
            const query = () => search().trim().toLowerCase();
            const entries = createMemo(() =>
              (sections().find((section) => section.title === title)?.entries ?? []).filter(
                (entry) =>
                  `${entry.label} ${entry.key} ${entry.description}`
                    .toLowerCase()
                    .includes(query()),
              ),
            );
            const keys = () => entries().map((entry) => entry.key);
            const selectedCount = () => keys().filter((key) => selected().has(key)).length;
            const all = () => keys().length > 0 && selectedCount() === keys().length;
            const some = () => selectedCount() > 0 && !all();
            return (
              <details
                open
                class="animated-card min-w-0 rounded-xl border border-neutral-200 dark:border-neutral-800"
              >
                <summary class="cursor-pointer rounded-xl bg-neutral-50 px-4 py-3 font-semibold focus-visible:outline-2 focus-visible:outline-brand-500 dark:bg-neutral-900/50">
                  {title}
                </summary>
                <div class="flex min-w-0 flex-col gap-3 p-3">
                  <Input
                    type="search"
                    label={`Search ${title} permissions`}
                    value={search()}
                    onInput={(e) => setSearch(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.preventDefault();
                    }}
                  />
                  <Show when={query()}>
                    <p class="text-xs text-neutral-500">
                      Select all applies only to matching permissions.
                    </p>
                  </Show>
                  <Checkbox
                    checked={all()}
                    indeterminate={some()}
                    disabled={props.readOnly || keys().length === 0}
                    onChange={() => setKeys(keys(), !all())}
                    aria-label={`Select all ${title} permissions`}
                    label={
                      title === 'Releases'
                        ? 'Select all global permissions'
                        : 'Select all permissions'
                    }
                  />
                  <Show
                    when={entries().length > 0}
                    fallback={<p role="status">No matching permissions.</p>}
                  >
                    <div class="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-800">
                      <table class="w-full text-sm">
                        <tbody>
                          <For each={entries()}>
                            {(entry) => (
                              <tr
                                class="border-t border-neutral-100 transition-colors hover:bg-neutral-50 motion-reduce:transition-none dark:border-neutral-800/60 dark:hover:bg-neutral-900/40"
                                classList={{ 'cursor-pointer': !props.readOnly }}
                                onClick={(e) => {
                                  // the checkbox label toggles by itself — don't double-toggle
                                  if ((e.target as HTMLElement).closest('label')) return;
                                  toggle(entry.key);
                                }}
                              >
                                <td class="px-3 py-2 align-top whitespace-nowrap">
                                  <Checkbox
                                    checked={selected().has(entry.key)}
                                    disabled={props.readOnly}
                                    onChange={() => toggle(entry.key)}
                                    label={
                                      <span class="flex flex-col">
                                        <span class="font-medium">{entry.label}</span>
                                        <code class="text-xs text-neutral-500 dark:text-neutral-400">
                                          {entry.key}
                                        </code>
                                      </span>
                                    }
                                  />
                                </td>
                                <td class="px-3 py-2 align-top text-neutral-600 dark:text-neutral-400">
                                  {entry.description}
                                </td>
                              </tr>
                            )}
                          </For>
                        </tbody>
                      </table>
                    </div>
                  </Show>
                  <Show
                    when={title === 'Releases' && (releaseProducts.data?.data ?? []).length > 0}
                  >
                    <ScopedReleaseGrants
                      products={releaseProducts.data?.data ?? []}
                      query={query()}
                      selected={selected()}
                      toggle={toggle}
                      setKeys={setKeys}
                      readOnly={props.readOnly}
                    />
                  </Show>
                </div>
              </details>
            );
          }}
        </For>
      </div>
      <Show when={error()}>
        <p class="text-xs text-red-600">{error()}</p>
      </Show>
      <div class="flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={props.onCancel}>
          {props.readOnly ? 'Back' : 'Cancel'}
        </Button>
        <Show when={!props.readOnly}>
          <Button type="submit" loading={loading()}>
            {props.role ? 'Save' : 'Create'}
          </Button>
        </Show>
      </div>
    </form>
  );
};
